// src/components/Logo.jsx
// Black Vanilla Technologies — Point Cloud Logo Mark + Wordmark
// Usage:
//   <Logo />                    → full horizontal logo (default, height 44px)
//   <Logo variant="mark" />     → cube icon only
//   <Logo size={32} />          → custom height in px
//   <Logo theme="light" />      → dark-dot version for light backgrounds

import React from 'react';

const MARK_VIEWBOX = "4 3 74 62";

function PointCloudMark({ theme = 'dark', style = {} }) {
  const accent  = '#C8F04A';
  const accentL = theme === 'dark' ? '#A8D040' : '#8BC020';
  const top     = '#C8F04A';

  // opacity multipliers per theme
  const rm = theme === 'dark' ? 1 : 0.9; // right face
  const lm = theme === 'dark' ? 1 : 0.6; // left face
  const tm = theme === 'dark' ? 1 : 1.0; // top face

  return (
    <svg viewBox={MARK_VIEWBOX} style={{ overflow: 'visible', ...style }}>
      <defs>
        <filter id="bv-glow">
          <feGaussianBlur stdDeviation="1.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={theme === 'dark' ? 'url(#bv-glow)' : undefined}>
        {/* ── RIGHT FACE (j=0) – brightest ── */}
        <circle cx="40" cy="38"   r="1.30" fill={accent} opacity={0.50 * rm} />
        <circle cx="40" cy="31"   r="1.45" fill={accent} opacity={0.57 * rm} />
        <circle cx="40" cy="24"   r="1.60" fill={accent} opacity={0.63 * rm} />
        <circle cx="40" cy="17"   r="1.75" fill={accent} opacity={0.70 * rm} />
        <circle cx="40" cy="10"   r="1.90" fill={accent} opacity={0.76 * rm} />
        <circle cx="46" cy="41.5" r="1.45" fill={accent} opacity={0.57 * rm} />
        <circle cx="46" cy="34.5" r="1.60" fill={accent} opacity={0.63 * rm} />
        <circle cx="46" cy="27.5" r="1.75" fill={accent} opacity={0.70 * rm} />
        <circle cx="46" cy="20.5" r="1.90" fill={accent} opacity={0.76 * rm} />
        <circle cx="46" cy="13.5" r="2.05" fill={accent} opacity={0.82 * rm} />
        <circle cx="52" cy="45"   r="1.60" fill={accent} opacity={0.63 * rm} />
        <circle cx="52" cy="38"   r="1.75" fill={accent} opacity={0.70 * rm} />
        <circle cx="52" cy="31"   r="1.90" fill={accent} opacity={0.76 * rm} />
        <circle cx="52" cy="24"   r="2.05" fill={accent} opacity={0.82 * rm} />
        <circle cx="52" cy="17"   r="2.20" fill={accent} opacity={0.89 * rm} />
        <circle cx="58" cy="48.5" r="1.75" fill={accent} opacity={0.70 * rm} />
        <circle cx="58" cy="41.5" r="1.90" fill={accent} opacity={0.76 * rm} />
        <circle cx="58" cy="34.5" r="2.05" fill={accent} opacity={0.82 * rm} />
        <circle cx="58" cy="27.5" r="2.20" fill={accent} opacity={0.89 * rm} />
        <circle cx="58" cy="20.5" r="2.35" fill={accent} opacity={0.95 * rm} />
        <circle cx="64" cy="52"   r="1.90" fill={accent} opacity={0.76 * rm} />
        <circle cx="64" cy="45"   r="2.05" fill={accent} opacity={0.82 * rm} />
        <circle cx="64" cy="38"   r="2.20" fill={accent} opacity={0.89 * rm} />
        <circle cx="64" cy="31"   r="2.35" fill={accent} opacity={0.95 * rm} />
        <circle cx="64" cy="24"   r="2.50" fill={accent} opacity={1.00 * rm} />

        {/* ── LEFT FACE (i=0, j=1–4) – dim ── */}
        <circle cx="34" cy="41.5" r="1.31" fill={accentL} opacity={0.35 * lm} />
        <circle cx="34" cy="34.5" r="1.43" fill={accentL} opacity={0.40 * lm} />
        <circle cx="34" cy="27.5" r="1.54" fill={accentL} opacity={0.45 * lm} />
        <circle cx="34" cy="20.5" r="1.65" fill={accentL} opacity={0.50 * lm} />
        <circle cx="34" cy="13.5" r="1.76" fill={accentL} opacity={0.56 * lm} />
        <circle cx="28" cy="45"   r="1.43" fill={accentL} opacity={0.40 * lm} />
        <circle cx="28" cy="38"   r="1.54" fill={accentL} opacity={0.45 * lm} />
        <circle cx="28" cy="31"   r="1.65" fill={accentL} opacity={0.50 * lm} />
        <circle cx="28" cy="24"   r="1.76" fill={accentL} opacity={0.56 * lm} />
        <circle cx="28" cy="17"   r="1.88" fill={accentL} opacity={0.61 * lm} />
        <circle cx="22" cy="48.5" r="1.54" fill={accentL} opacity={0.45 * lm} />
        <circle cx="22" cy="41.5" r="1.65" fill={accentL} opacity={0.50 * lm} />
        <circle cx="22" cy="34.5" r="1.76" fill={accentL} opacity={0.56 * lm} />
        <circle cx="22" cy="27.5" r="1.88" fill={accentL} opacity={0.61 * lm} />
        <circle cx="22" cy="20.5" r="1.99" fill={accentL} opacity={0.66 * lm} />
        <circle cx="16" cy="52"   r="1.65" fill={accentL} opacity={0.50 * lm} />
        <circle cx="16" cy="45"   r="1.76" fill={accentL} opacity={0.56 * lm} />
        <circle cx="16" cy="38"   r="1.88" fill={accentL} opacity={0.61 * lm} />
        <circle cx="16" cy="31"   r="1.99" fill={accentL} opacity={0.66 * lm} />
        <circle cx="16" cy="24"   r="2.10" fill={accentL} opacity={0.71 * lm} />

        {/* ── TOP FACE (k=4) – bright ── */}
        <circle cx="40" cy="17"   r="2.00" fill={top} opacity={0.91 * tm} />
        <circle cx="34" cy="20.5" r="1.90" fill={top} opacity={0.87 * tm} />
        <circle cx="28" cy="24"   r="1.80" fill={top} opacity={0.83 * tm} />
        <circle cx="22" cy="27.5" r="1.70" fill={top} opacity={0.78 * tm} />
        <circle cx="46" cy="20.5" r="1.90" fill={top} opacity={0.87 * tm} />
        <circle cx="40" cy="24"   r="1.80" fill={top} opacity={0.83 * tm} />
        <circle cx="34" cy="27.5" r="1.70" fill={top} opacity={0.78 * tm} />
        <circle cx="28" cy="31"   r="1.60" fill={top} opacity={0.74 * tm} />
        <circle cx="52" cy="24"   r="1.80" fill={top} opacity={0.83 * tm} />
        <circle cx="46" cy="27.5" r="1.70" fill={top} opacity={0.78 * tm} />
        <circle cx="40" cy="31"   r="1.60" fill={top} opacity={0.74 * tm} />
        <circle cx="34" cy="34.5" r="1.50" fill={top} opacity={0.70 * tm} />
        <circle cx="58" cy="27.5" r="1.70" fill={top} opacity={0.78 * tm} />
        <circle cx="52" cy="31"   r="1.60" fill={top} opacity={0.74 * tm} />
        <circle cx="46" cy="34.5" r="1.50" fill={top} opacity={0.70 * tm} />
        <circle cx="40" cy="38"   r="1.40" fill={top} opacity={0.65 * tm} />

        {/* ── Atmospheric scatter ── */}
        <circle cx="13" cy="19"   r="1.0" fill={accent} opacity={0.20 * rm} />
        <circle cx="70" cy="14"   r="0.8" fill={accent} opacity={0.17 * rm} />
        <circle cx="10" cy="44"   r="0.9" fill={accent} opacity={0.15 * rm} />
        <circle cx="68" cy="47"   r="1.0" fill={accent} opacity={0.18 * rm} />
        <circle cx="38" cy="6"    r="0.8" fill={accent} opacity={0.27 * rm} />
        <circle cx="43" cy="6"    r="1.1" fill={accent} opacity={0.17 * rm} />
        <circle cx="19" cy="58"   r="0.9" fill={accent} opacity={0.17 * rm} />
        <circle cx="61" cy="58"   r="1.0" fill={accent} opacity={0.20 * rm} />
        <circle cx="75" cy="31"   r="0.7" fill={accent} opacity={0.14 * rm} />
        <circle cx="6"  cy="32"   r="0.8" fill={accent} opacity={0.12 * rm} />
        <circle cx="73" cy="42"   r="0.9" fill={accent} opacity={0.17 * rm} />
        <circle cx="7"  cy="21"   r="0.7" fill={accent} opacity={0.12 * rm} />
      </g>
    </svg>
  );
}

export default function Logo({
  variant = 'full',   // 'full' | 'mark'
  size    = 44,       // height in px (mark height; wordmark scales with it)
  theme   = 'dark',  // 'dark' | 'light'
  style   = {},
}) {
  const markH = size;
  const markW = markH * (74 / 62);   // preserve aspect of viewBox

  const nameSize = markH * 0.48;
  const subSize  = markH * 0.26;
  const gap      = markH * 0.14;

  const nameColor = theme === 'dark' ? '#C8F04A' : '#0A0A0A';
  const subColor = theme === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(10,10,10,0.48)';

  if (variant === 'mark') {
    return (
      <PointCloudMark
        theme={theme}
        style={{ width: markW, height: markH, ...style }}
      />
    );
  }

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: markH * 0.38,
      ...style,
    }}>
      <PointCloudMark
        theme={theme}
        style={{ width: markW, height: markH, flexShrink: 0 }}
      />
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap,
        lineHeight: 1,
      }}>
        <span style={{
          fontFamily: "'Inter', 'DM Sans', sans-serif",
          fontWeight: 800,
          fontSize: nameSize,
          letterSpacing: '0.04em',
          color: nameColor,
          whiteSpace: 'nowrap',
        }}>
          BLACK VANILLA
        </span>
        <span style={{
          fontFamily: "'Inter', 'DM Sans', sans-serif",
          fontWeight: 500,
          fontSize: subSize,
          letterSpacing: '0.22em',
          color: subColor,
          whiteSpace: 'nowrap',
        }}>
          PHYSICAL INTELLIGENCE
        </span>
      </div>
    </div>
  );
}
